import React from 'react';
import { Box, Button, Typography, Divider, Grid, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { styled } from '@mui/material/styles';
import LinearProgress, {
  linearProgressClasses,
} from '@mui/material/LinearProgress';


const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor:
      theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: theme.palette.mode === 'light' ? '#c22027' : '#c22027',
  },
}));

interface TimeTrackingProps {
  caseData: any;
  totalTime: number;
  remainingTimePercent: number;
  loggedTimePercent: number;
  loggedTime: any[];
  timeEstimate: string;
  handleLogTimeModalOpen: (value: boolean) => void;
  handleEditLogTimeModalOpen: (event: React.MouseEvent, item: any) => void;
  handleDeleteLogTimeModalOpen: (item: any) => void;
  convertSecondsToTimeString: (seconds: number) => string;
}

const TimeTracking: React.FC<TimeTrackingProps> = ({
  caseData,
  totalTime,
  remainingTimePercent,
  loggedTimePercent,
  loggedTime,
  timeEstimate,
  handleLogTimeModalOpen,
  handleEditLogTimeModalOpen,
  handleDeleteLogTimeModalOpen,
  convertSecondsToTimeString,
}) => (
  <Box component="div" style={{ padding: '8px' }}>
    <Box
      display="flex"
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center">
      <Typography gutterBottom variant="h6" style={{ fontSize: '14px' }}>
        Time Tracking
      </Typography>
      {(!caseData?.caseKey?.startsWith('AFR') ||
        (caseData?.caseKey?.startsWith('AFR') && caseData?.isApproved) ||
        !caseData?.isApprovalRequired) &&
        totalTime > 0 &&
        !!caseData?.billingAccountType && (
          <div className="jiraSync">
            <Button
              variant="contained"
              color="secondary"
              sx={{
                padding: '4px 8px',
                fontSize: '12px',
              }}
              onClick={() => handleLogTimeModalOpen(true)}>
              New Worklog
            </Button>
          </div>
        )}
    </Box>
    <Divider sx={{ margin: '8px 0' }} />

    {caseData?.caseKey?.startsWith('AFR') &&
    caseData?.isApprovalRequired &&
    !caseData?.isApproved ? (
      <Grid container>
        <Grid
          item
          xs={12}
          style={{
            textAlign: 'center',
            fontSize: '12px',
            color: '#c22027',
          }}>
          The case is not approved.
        </Grid>
      </Grid>
    ) : (
      totalTime <= 0 && (
        <Grid container>
          <Grid
            item
            xs={12}
            style={{
              textAlign: 'center',
              fontSize: '12px',
            }}>
            Please add time estimate and account to add worklog.
          </Grid>
        </Grid>
      )
    )}

    {(!caseData?.caseKey?.startsWith('AFR') ||
      (caseData?.caseKey?.startsWith('AFR') &&
        (caseData?.isApproved || !caseData?.isApprovalRequired))) &&
      totalTime > 0 &&
      !!caseData?.billingAccountType && (
        <Grid container xs={12} spacing={1} alignItems="center">
          <Grid item xs={4} style={{ fontSize: '12px' }}>
            Estimated
          </Grid>
          <Grid item xs={8}>
            <Box color="#c22027">
              <BorderLinearProgress
                style={{ height: '8px' }}
                variant="determinate"
                value={(totalTime / totalTime) * 100}
              />
            </Box>
          </Grid>
          <Grid item xs={4} style={{ fontSize: '12px' }}>
            Remaining
          </Grid>
          <Grid item xs={8}>
            <Box
              color="#c22027"
              sx={{
                transform: 'scaleX(-1)',
                justifyContent: 'flex-end',
              }}>
              <BorderLinearProgress
                style={{ height: '8px' }}
                variant="determinate"
                value={remainingTimePercent}
              />
            </Box>
          </Grid>
          <Grid item xs={4} style={{ fontSize: '12px' }}>
            Logged
          </Grid>
          <Grid item xs={8}>
            <Box color="#c22027">
              <BorderLinearProgress
                style={{ height: '8px' }}
                variant="determinate"
                value={loggedTimePercent}
              />
            </Box>
          </Grid>
          {loggedTime?.length > 0 && (
            <>
              <Grid item xs={12}>
                <Divider />
                <Typography
                  gutterBottom
                  marginTop="8px"
                  style={{ fontSize: '14px' }}>
                  Breakdown
                </Typography>
              </Grid>
              {loggedTime.map((item) => {
                let progress = (item?.loggedHoursInSeconds / totalTime) * 100;
                if (progress > 100) progress = 100;
                return (
                  <React.Fragment key={item?.id}>
                    <Grid item xs={3} style={{ fontSize: '12px' }}>
                      {item?.createdBy.name}
                    </Grid>
                    <Grid item xs={9}>
                      <Grid container alignItems="center" spacing={1}>
                        <Grid item xs={7}>
                          <Tooltip title={item?.description || ''}>
                            <BorderLinearProgress
                              style={{ height: '8px' }}
                              variant="determinate"
                              value={progress}
                            />
                          </Tooltip>
                        </Grid>
                        <Grid item xs={3} style={{ fontSize: '12px' }}>
                          {convertSecondsToTimeString(
                            item?.loggedHoursInSeconds
                          )}{' '}
                          / {timeEstimate}
                        </Grid>
                        <Grid item xs={2} display="flex">
                          <EditIcon
                            sx={{
                              cursor: 'pointer',
                              fontSize: '16px',
                              ':hover': {
                                color: '#C22027',
                              },
                            }}
                            onClick={(event) =>
                              handleEditLogTimeModalOpen(event, item)
                            }
                          />
                          <DeleteIcon
                            sx={{
                              cursor: 'pointer',
                              fontSize: '16px',
                              marginLeft: '10px',
                              ':hover': {
                                color: '#C22027',
                              },
                            }}
                            onClick={() => {
                              handleDeleteLogTimeModalOpen(item);
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </React.Fragment>
                );
              })}
            </>
          )}
        </Grid>
      )}
  </Box>
);

export default TimeTracking;
